import { get, pickBy } from 'lodash';
import endpoints from '../../utils/endpoints';
import { getReference } from './common';
import { CONST_FILE_UPLOAD } from '../../config/constants';

export const getBeneficiaryCreateRequest = (bankId) => (payment) => {
	const uri = endpoints.beneficiaries.resolve(bankId, 'default', payment.fromAccount);
	const { x_bpi_version, method } = endpoints.beneficiaries;

	return {
		id: payment.id,
		method,
		uri,
		x_bpi_version,
		body: {
			name: get(payment, 'displayName'),
			display_name: get(payment, 'displayName'),
			account_id: get(payment, 'toAccount'),
			...getReference(payment),
			single_use: true,
		},
	};
};

export const getAddNewPayeeRequest = (
	{
		accountNumber,
		sortCode,
		displayName = '',
		reference,
		singleUse,
		chapsPaymentsEnable,
		chapsReference,
		payeeAddressLine1,
		payeeAddressLine2,
		payeeCity,
		payeePostalCode,
		payeeCountry,
		name = '',
	},
	cop
) => {
	const chapsDetails = chapsPaymentsEnable
		? {
				type: 'chaps',
				reference_high_value: chapsReference,
				address: {
					address_line_1: payeeAddressLine1,
					address_line_2: payeeAddressLine2,
					city: payeeCity,
					postal_code: payeePostalCode,
					country: payeeCountry,
				},
		  }
		: {};

	const pickByPredicate = (value, key) => key === 'display_name' || value !== '';

	const request = pickBy(
		{
			display_name: displayName,
			account_number: accountNumber,
			sort_code: sortCode.replace(/-/g, '') || '',
			name: get(cop, 'payeeName', '').substring(0, 50) || name.substring(0, 50) || displayName.substring(0, 50),
			reference,
			single_use: singleUse,
			cop_account_name: get(cop, 'payeeName'),
			cop_account_type: get(cop, 'payeeType'),
			confirmation_of_payee: get(cop, 'result'),
			...chapsDetails,
		},
		pickByPredicate
	);

	return JSON.stringify(request);
};

export const getBeneficiaryCreateEventAttribute = ({
	linkAccount,
	name,
	sortCode,
	accountNumber,
	displayName = '',
	reference,
	singleUse,
}) => ({
	name,
	account_id: linkAccount,
	sort_code: sortCode,
	account_number: accountNumber,
	display_name: displayName,
	reference,
	single_use: singleUse,
});
export const getBeneficiaryUpdateEventAttribute = ({
	linkAccount,
	name,
	sortCode,
	oldDetails = {},
	accountNumber,
	displayName = '',
	reference,
	singleUse,
}) => ({
	name,
	account_id: linkAccount,
	old_sort_code: oldDetails.sort_code,
	new_sort_code: sortCode,
	old_account_number: oldDetails.account_number,
	new_account_number: accountNumber,
	display_name: displayName,
	reference,
	single_use: singleUse,
});

export const getAddInternationalNewPayeeRequest = ({
	internationalPayeeName,
	internationalAccountNumber,
	displayName = '',
	singleUse = false,
	iban = '',
	bickOrSwift = '',
	clearingCode,
	paymentReason,
	payeeAddressLine1,
	payeeAddressLine2,
	payeeCity,
	payeePostalCode,
	payeeCountry,
	bankName,
	bankAddressLine1,
	bankAddressLine2,
	bankCity,
	bankPostalCode,
	bankCountry,
}) => {
	const bank = {
		name: bankName,
		address: {
			address_line_1: bankAddressLine1,
			address_line_2: bankAddressLine2,
			city: bankCity,
			postal_code: bankPostalCode,
			country: bankCountry,
		},
	};
	const addPayeeRequest = {
		name: internationalPayeeName,
		account_number: internationalAccountNumber,
		single_use: singleUse,
		iban: iban.toUpperCase(),
		bic: bickOrSwift.toUpperCase(),
		clearing_code: clearingCode,
		reference_high_value: paymentReason,
		address: {
			address_line_1: payeeAddressLine1,
			address_line_2: payeeAddressLine2,
			city: payeeCity,
			postal_code: payeePostalCode,
			country: payeeCountry,
		},
		bank: bankName ? bank : undefined,
	};
	const compactAddPayeeRequest = pickBy(addPayeeRequest, (value) => value !== '');
	compactAddPayeeRequest.display_name = displayName;
	return JSON.stringify(compactAddPayeeRequest);
};

export const getBeneficiaryCreateRequestForFileImport =
	(bankId, accountId, isSingleUse = false) =>
	(importedPayee) => {
		const uri = endpoints.beneficiaries.resolve(bankId, 'default', accountId);
		const { x_bpi_version, method } = endpoints.beneficiaries;
		const { id, name, sort_code, account_number, display_name, reference, reference_high_value } = importedPayee;
		const batch_type = CONST_FILE_UPLOAD.BATCH_TYPE;
		const fpsReference = reference && isSingleUse ? batch_type : reference;
		const chapsReference = reference_high_value && isSingleUse ? batch_type : reference_high_value;
		return {
			id,
			method,
			uri,
			x_bpi_version,
			body: {
				name,
				sort_code,
				account_number,
				display_name: isSingleUse ? '' : display_name,
				reference: fpsReference,
				reference_high_value: chapsReference,
				single_use: isSingleUse,
				batch_type,
			},
		};
	};

export const getBeneficiaryCreateRequestForFileImportWithFromAccount =
	(bankId, isSingleUse = false) =>
	(importedPayee) => {
		const { fromAccount, id, name, sort_code, account_number, display_name, reference, reference_high_value } =
			importedPayee;
		const uri = endpoints.beneficiaries.resolve(bankId, 'default', fromAccount);
		const { x_bpi_version, method } = endpoints.beneficiaries;
		const batch_type = CONST_FILE_UPLOAD.BATCH_TYPE;
		const fpsReference = reference && isSingleUse ? batch_type : reference;
		const chapsReference = reference_high_value && isSingleUse ? batch_type : reference_high_value;
		return {
			id,
			method,
			uri,
			x_bpi_version,
			body: {
				name,
				sort_code,
				account_number,
				display_name: isSingleUse ? '' : display_name,
				reference: fpsReference,
				reference_high_value: chapsReference,
				single_use: isSingleUse,
				batch_type,
			},
		};
	};
